import { PAGE_CONTENT_V_OFFSET } from '@sleepstation/metrics-public-site'
import { marginTop, stylesIfPropTrue } from '@sleepstation/style-api-helpers'
import PropTypes from 'prop-types'
import React from 'react'
import { useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import HeadTags from '../components/HeadTags'
import Banners from '../components/Site/Banners'
import CookieConsentNotice from '../components/Site/CookieConsentNotice'
import Site from '../components/Site/Site'
import SiteBody from '../components/Site/SiteBody'
import StickyNotice from '../components/Site/StickyNotice'
import SiteHeaderDefault from '../components/Site/headers/SiteHeaderDefault'
import COOKIE_IDS from '../const/cookieIds'
import METADATA from '../data/metadata'
import TRACKING from '../data/tracking'
import { findCookieById } from '../helpers/cookies'
import useIsAuthorised from '../hooks/useIsAuthorised'
import { getEnvironment } from '../utils/environment'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const { CONSENT } = COOKIE_IDS

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const resolveAuthenticationConfig = (config, pageData) => {
  if (!config) return null

  const { redirectUrlInvalid, redirectUrlValid } = config

  const { context } = pageData.portal

  return {
    context,
    redirectUrlInvalid,
    redirectUrlValid,
  }
}

const renderHeadTags = (pageData, location) => {
  const {
    page: { metadata },
  } = pageData

  const { HOST } = getEnvironment()

  const resolvedMetaImageURLRelative = METADATA.defaultImage
  const resolvedMetaImageURLAbsolute = `${HOST.SELF}${resolvedMetaImageURLRelative}`

  // eslint-disable-next-line react/prop-types
  const { pathname } = location
  const { title, description, shouldIndex, canonical } = metadata

  return (
    <HeadTags
      pagePath={pathname}
      title={title}
      description={description}
      shouldIndex={shouldIndex}
      canonical={canonical}
      imageUrl={resolvedMetaImageURLAbsolute}
    />
  )
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const SiteBodyFooterWrapper = styled.div`
  /* Note we don’t use a VLayout here as it would overwrite the footer's top-margin */
  position: relative;
  display: flex;
  flex-direction: column;

  /* ensure the footer is at the bottom of the screen */
  flex-grow: 2 !important;
  z-index: 1;

  > *:first-child {
    z-index: 1;
  }

  > *:last-child {
    z-index: 2;
  }

  ${stylesIfPropTrue(
    `shouldOffsetContent`,
    css`
      ${marginTop(PAGE_CONTENT_V_OFFSET)}
    `
  )}
`

const SiteHeaderWrapper = styled.div`
  position: relative;
  z-index: 2;
  ${stylesIfPropTrue(
    `shouldFixHeader`,
    css`
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
    `
  )}
`

const SiteInner = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex-grow: 2;
  justify-items: stretch;
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const DefaultPageLayout = ({
  children,
  pageData,
  SiteFooter,
  SiteHeader,
  shouldFixHeader,
  shouldOffsetContent,
  footerMargin,
  showFooter,
  hidePrimaryNav,
  authenticationConfig,
  termsAndConditionsPath,
  informationStandardPath,
  privacyPolicyPath,
}) => {
  const { cookies, referrers } = TRACKING
  const { keys: referrerKeys } = referrers
  const { DOMAIN } = getEnvironment()
  const location = useLocation()

  const resolvedAuthenticationConfig = resolveAuthenticationConfig(
    authenticationConfig,
    pageData
  )

  const {
    page: { tracking, banners },
  } = pageData

  const isAuthorised = useIsAuthorised(resolvedAuthenticationConfig)

  // Even if we are not authorised and a redirect is triggered, page content
  // will render briefly. Returning nothing is the safest and least disruptive
  // option.
  if (!isAuthorised) {
    return null
  }

  const consent = findCookieById(CONSENT, cookies)

  return (
    <React.Fragment>
      <StickyNotice>
        <CookieConsentNotice
          body={consent.body}
          domain={DOMAIN}
          cookieName={consent.name}
        />
      </StickyNotice>
      <Site referrerKeys={referrerKeys} domain={DOMAIN} tracking={tracking}>
        <Banners
          cookies={cookies}
          domain={DOMAIN}
          banners={banners}
          pageData={pageData}
        />
        <SiteInner>
          <SiteHeaderWrapper shouldFixHeader={shouldFixHeader}>
            <SiteHeader
              hideBackground={shouldFixHeader}
              hideNav={hidePrimaryNav}
            />
          </SiteHeaderWrapper>
          <SiteBodyFooterWrapper
            shouldOffsetContent={shouldOffsetContent && !shouldFixHeader}
          >
            <SiteBody>{children}</SiteBody>
            {showFooter && SiteFooter && (
              <SiteFooter
                termsAndConditionsPath={termsAndConditionsPath}
                privacyPolicyPath={privacyPolicyPath}
                informationStandardPath={informationStandardPath}
                margin={footerMargin}
              />
            )}
          </SiteBodyFooterWrapper>
        </SiteInner>
      </Site>
      {renderHeadTags(pageData, location)}
    </React.Fragment>
  )
}

DefaultPageLayout.propTypes = {
  pageData: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  SiteFooter: PropTypes.func,
  SiteHeader: PropTypes.func,
  showFooter: PropTypes.bool,
  shouldFixHeader: PropTypes.bool,
  shouldOffsetContent: PropTypes.bool,
  footerMargin: PropTypes.number,
  authenticationConfig: PropTypes.object,
  termsAndConditionsPath: PropTypes.string,
  informationStandardPath: PropTypes.string,
  privacyPolicyPath: PropTypes.string,
  hidePrimaryNav: PropTypes.bool,
  banners: PropTypes.object,
}

DefaultPageLayout.defaultProps = {
  SiteFooter: undefined,
  SiteHeader: SiteHeaderDefault,
  showFooter: true,
  shouldFixHeader: false,
  hidePrimaryNav: false,
  shouldOffsetContent: true,
  footerMargin: undefined,
  authenticationConfig: undefined,
  termsAndConditionsPath: undefined,
  informationStandardPath: undefined,
  privacyPolicyPath: undefined,
  banners: {},
}

export default DefaultPageLayout
